import { ANALYTICS_EVENTS, ANALYTICS_CONFIG } from "./constants.js";

export class AnalyticsCore {
  constructor(config = {}) {
    this.config = { ...ANALYTICS_CONFIG, ...config };
    this.eventQueue = [];
    this.userId = null;
    this.userProperties = {};
    this.sessionId = this.generateSessionId();
  }

  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  setUserId(userId) {
    this.userId = userId;
  }

  setUserProperties(properties) {
    this.userProperties = { ...this.userProperties, ...properties };
  }

  validateEvent(eventName, properties = {}) {
    if (!eventName || typeof eventName !== "string") {
      console.error("Analytics: Invalid event name", eventName);
      if (this.config.DEBUG_MODE) {
        console.trace("Analytics validation error trace");
      }
      return false;
    }

    if (properties && typeof properties !== "object") {
      console.error("Analytics: Properties must be an object", properties);
      return false;
    }

    // Check for null/undefined values in properties
    if (properties) {
      const invalidProps = Object.entries(properties).filter(
        ([key, value]) => value === null || value === undefined,
      );
      if (invalidProps.length > 0) {
        console.warn(
          "Analytics: Filtering out null/undefined properties:",
          invalidProps.map(([key]) => key),
        );
        // Filter out invalid properties
        properties = Object.fromEntries(
          Object.entries(properties).filter(
            ([key, value]) => value !== null && value !== undefined,
          ),
        );
      }
    }

    return true;
  }

  createEvent(eventName, properties = {}) {
    if (!this.validateEvent(eventName, properties)) {
      return null;
    }

    // Filter out null/undefined properties
    const cleanProperties = properties
      ? Object.fromEntries(
          Object.entries(properties).filter(
            ([key, value]) => value !== null && value !== undefined,
          ),
        )
      : {};

    // Generate unique event ID
    const eventId = `evt_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    return {
      event_id: eventId,
      timestamp: new Date().toISOString(),
      event_name: eventName,
      session_id: this.sessionId,
      user_id: this.userId,
      platform: "web", // Default platform, will be overridden by adapters
      user_properties: { ...this.userProperties },
      session_properties: {},
      device_properties: {},
      event_properties: { ...cleanProperties },
      ingestion_source: "web_app" // Default source, will be overridden by adapters
    };
  }

  track(eventName, properties = {}) {
    const event = this.createEvent(eventName, properties);
    if (!event) {
      if (this.config.DEBUG_MODE) {
        console.error("Analytics: Skipping invalid event", {
          eventName,
          properties,
        });
        console.trace("Event creation failed at:");
      }
      return false;
    }

    this.eventQueue.push(event);

    if (this.config.DEBUG_MODE) {
      console.log("Analytics tracked:", event);
    }

    return true;
  }

  trackCustomEvent(eventName, properties = {}) {
    return this.track(eventName, properties);
  }

  trackScreenView(screenName, properties = {}) {
    return this.track(ANALYTICS_EVENTS.SCREEN_VIEW, {
      screen_name: screenName,
      ...properties,
    });
  }

  trackPerformance(metricName, value, properties = {}) {
    // Validate performance metrics
    if (value === null || value === undefined || typeof value !== "number") {
      if (this.config.DEBUG_MODE) {
        console.warn("Analytics: Invalid performance metric value", {
          metricName,
          value,
        });
      }
      return false;
    }

    return this.track(ANALYTICS_EVENTS.PAGE_LOAD_TIME, {
      metric_name: metricName,
      value: value,
      ...properties,
    });
  }

  trackError(error, properties = {}) {
    const errorInfo = {
      error_message: error.message || "Unknown error",
      error_stack: error.stack || "No stack trace",
      ...properties,
    };

    return this.track(ANALYTICS_EVENTS.ERROR_OCCURRED, errorInfo);
  }

  getQueuedEvents() {
    return [...this.eventQueue];
  }

  clearQueue() {
    this.eventQueue = [];
  }

  flush() {
    // Override in platform-specific adapters
    console.warn(
      "Analytics: flush() method should be implemented by platform adapter",
    );
  }
}

export { ANALYTICS_EVENTS, ANALYTICS_CONFIG };
